/**
 * Minimal OTLP/HTTP JSON exporter for browser-side spans.
 *
 * Posts batches to `${origin}${endpointPath}` (same-origin → no CORS
 * preflight). The gateway rewrites that to `/v1/traces` and forwards to
 * Alloy's OTLP HTTP receiver on port 4318.
 *
 * We intentionally don't pull in `@opentelemetry/sdk-trace-web` —
 * bundle weight isn't justified for what amounts to "build the OTLP JSON
 * envelope, POST it". The shape below matches OTLP/HTTP v1.
 */

import type { ActionBreadcrumb } from './actionTracker';

export interface BrowserSpan {
  traceId: string;
  spanId: string;
  parentSpanId?: string;
  name: string;
  /** OTel SpanKind: 1=INTERNAL, 2=SERVER, 3=CLIENT, 4=PRODUCER, 5=CONSUMER */
  kind: number;
  startTimeMs: number;
  endTimeMs: number;
  attributes: Record<string, string | number | boolean | undefined>;
  /** OTel status: 0=UNSET, 1=OK, 2=ERROR */
  statusCode?: 0 | 1 | 2;
  statusMessage?: string;
  events?: Array<{ ts: number; name: string; attributes?: Record<string, string | number | boolean> }>;
}

export interface OtlpExporterConfig {
  serviceName: string;
  serviceVersion?: string;
  /** Same-origin path the gateway routes to alloy. */
  endpointPath: string;
  /** Force-flush after this many spans accumulate. */
  maxBatch?: number;
  /** Or after this many ms, whichever comes first. */
  flushIntervalMs?: number;
}

interface Resolved extends Required<Omit<OtlpExporterConfig, 'serviceVersion'>> {
  serviceVersion: string;
}

const queue: BrowserSpan[] = [];
let cfg: Resolved | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function attrValue(v: string | number | boolean | undefined) {
  if (typeof v === 'string') return { stringValue: v };
  if (typeof v === 'number') {
    return Number.isInteger(v) ? { intValue: v } : { doubleValue: v };
  }
  if (typeof v === 'boolean') return { boolValue: v };
  return { stringValue: '' };
}

function toAttrList(attrs: Record<string, string | number | boolean | undefined>) {
  return Object.entries(attrs)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([key, value]) => ({ key, value: attrValue(value) }));
}

function buildPayload(spans: BrowserSpan[]) {
  if (!cfg) return null;
  return {
    resourceSpans: [
      {
        resource: {
          attributes: toAttrList({
            'service.name': cfg.serviceName,
            'service.version': cfg.serviceVersion,
            'telemetry.sdk.language': 'webjs',
            'telemetry.sdk.name': 'clipsymphony-browser',
            'browser.user_agent':
              typeof window !== 'undefined' && window.navigator
                ? window.navigator.userAgent
                : '',
          }),
        },
        scopeSpans: [
          {
            scope: { name: 'clipsymphony-browser', version: '1.0.0' },
            spans: spans.map((s) => ({
              traceId: s.traceId,
              spanId: s.spanId,
              parentSpanId: s.parentSpanId,
              name: s.name,
              kind: s.kind,
              startTimeUnixNano: `${Math.floor(s.startTimeMs)}000000`,
              endTimeUnixNano: `${Math.floor(s.endTimeMs)}000000`,
              attributes: toAttrList(s.attributes),
              status: {
                code: s.statusCode ?? 0,
                message: s.statusMessage,
              },
              events: (s.events ?? []).map((e) => ({
                timeUnixNano: `${Math.floor(e.ts)}000000`,
                name: e.name,
                attributes: e.attributes ? toAttrList(e.attributes) : [],
              })),
            })),
          },
        ],
      },
    ],
  };
}

function sendBeaconOrFetch(body: string): void {
  if (!cfg) return;
  const url = cfg.endpointPath;
  // sendBeacon is async, fire-and-forget, and survives page unload. The
  // browser caps payload size (~64KB on most engines) — our batches stay
  // well under that.
  if (
    typeof window !== 'undefined' &&
    window.navigator &&
    typeof window.navigator.sendBeacon === 'function'
  ) {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      if (window.navigator.sendBeacon(url, blob)) return;
    } catch {
      /* fall through to fetch */
    }
  }
  // Fallback: keepalive fetch so the request still flushes during unload.
  fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    /* swallow — losing a trace must not break the page */
  });
}

function flush(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!cfg || queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  const payload = buildPayload(batch);
  if (!payload) return;
  try {
    sendBeaconOrFetch(JSON.stringify(payload));
  } catch {
    /* never throw out of the exporter */
  }
}

function scheduleFlush(): void {
  if (!cfg || flushTimer) return;
  flushTimer = setTimeout(flush, cfg.flushIntervalMs);
}

/**
 * Queues a span for export. Triggers a flush when the batch fills, or
 * after `flushIntervalMs`, or immediately on page-hide.
 */
export function recordBrowserSpan(span: BrowserSpan): void {
  if (!cfg) return;
  queue.push(span);
  if (queue.length >= cfg.maxBatch) {
    flush();
  } else {
    scheduleFlush();
  }
}

/**
 * Encode breadcrumbs as a single span attribute. JSON keeps it queryable
 * in Tempo's attribute filters; truncate so a single noisy session can't
 * blow past the OTLP attribute size cap.
 */
export function encodeBreadcrumbs(crumbs: ActionBreadcrumb[]): string {
  const json = JSON.stringify(crumbs);
  return json.length > 1024 ? json.slice(0, 1021) + '...' : json;
}

/**
 * Initialize the exporter. Subsequent calls overwrite config but don't
 * tear down listeners. Returns false in non-browser environments.
 */
export function initOtlpExporter(config: OtlpExporterConfig): boolean {
  if (typeof window === 'undefined') return false;
  cfg = {
    serviceName: config.serviceName,
    serviceVersion: config.serviceVersion ?? '0.0.0',
    endpointPath: config.endpointPath,
    maxBatch: config.maxBatch ?? 20,
    flushIntervalMs: config.flushIntervalMs ?? 5000,
  };

  // Flush on tab hide (mobile especially), unload, and pagehide. Beacon
  // works in all three contexts; fetch+keepalive is the fallback.
  const flushOnHide = () => flush();
  window.document.addEventListener('visibilitychange', () => {
    if (window.document.visibilityState === 'hidden') flushOnHide();
  });
  window.addEventListener('pagehide', flushOnHide);
  window.addEventListener('beforeunload', flushOnHide);
  return true;
}
